import { create } from 'zustand';
import { reportApi } from '../services/api';
import type { Report, ReportData } from '../types';
import type { PagedData, ListQueryRequest } from '../types';

interface ReportState {
  // 状态
  reports: Report[];
  currentReport: Report | null;
  reportData: ReportData[];
  total: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchReports: (params: ListQueryRequest) => Promise<void>;
  fetchReportById: (id: string) => Promise<void>;
  createReport: (data: Partial<Report>) => Promise<void>;
  updateReport: (id: string, data: Partial<Report>) => Promise<void>;
  submitReport: (id: string) => Promise<void>;
  approveReport: (id: string, comment?: string) => Promise<void>;
  rejectReport: (id: string, reason: string) => Promise<void>;
  deleteReport: (id: string) => Promise<void>;
  setCurrentReport: (report: Report | null) => void;
}

export const useReportStore = create<ReportState>((set, get) => ({
  reports: [],
  currentReport: null,
  reportData: [],
  total: 0,
  isLoading: false,
  error: null,

  fetchReports: async (params: ListQueryRequest) => {
    console.log('📡 fetchReports 被调用:', params);
    set({ isLoading: true, error: null });
    try {
      const data = await reportApi.getReports(params);
      console.log('✅ fetchReports 获取到数据:', data.items?.length, '条');
      set({ 
        reports: data.items || [], 
        total: data.total || 0,
        isLoading: false 
      });
    } catch (error: any) {
      console.error('❌ fetchReports 失败:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  fetchReportById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const report = await reportApi.getReportById(id);
      set({ 
        currentReport: report, 
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  createReport: async (data: Partial<Report>) => {
    set({ isLoading: true, error: null });
    try {
      await reportApi.createReport(data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  updateReport: async (id: string, data: Partial<Report>) => {
    set({ isLoading: true, error: null });
    try {
      await reportApi.updateReport(id, data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  submitReport: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await reportApi.submitReport(id);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  approveReport: async (id: string, comment?: string) => {
    set({ isLoading: true, error: null });
    try {
      await reportApi.approveReport(id, { comment });
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  rejectReport: async (id: string, reason: string) => {
    set({ isLoading: true, error: null });
    try {
      await reportApi.rejectReport(id, { reason });
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  deleteReport: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await reportApi.deleteReport(id);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  setCurrentReport: (report: Report | null) => {
    set({ currentReport: report });
  },
}));
